import React, { useEffect, useState } from 'react'
import { Row, Col, Card, CardBody, Button, Badge, Input, Label, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup } from 'reactstrap'
import { getTimingsRequest, markDQRequest, markFinishedRequest, markTBCRequest, markDNFRequest, resetTimingRequest } from '../../../api/controller/DiagnosticsController'
import { getRaceListRequest } from '../../../api/controller/ExtController'

const statuses = [
  { key: 'Finished', color: 'success' },
  { key: 'TBC', color: 'warning' },
  { key: 'DNF', color: 'secondary' },
  { key: 'DQ', color: 'danger' },
  { key: 'DNS', color: 'dark' },
]

export default function Diagnostics() {
  const [loading, setLoading] = useState(false)
  const [raceOptions, setRaceOptions] = useState([])
  const [race, setRace] = useState(null)
  const [selectedStatuses, setSelectedStatuses] = useState(['TBC'])
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState([])
  const [finishOpen, setFinishOpen] = useState(false)
  const [finishRow, setFinishRow] = useState(null)
  const [chipStartTime, setChipStartTime] = useState('')
  const [chipEndTime, setChipEndTime] = useState('')
  const [hadTimes, setHadTimes] = useState(false)

  useEffect(() => {
    (async ()=>{
      const races = await getRaceListRequest({})
      setRaceOptions(races?.data || [])
    })()
  }, [])

  const load = async () => {
    setLoading(true)
    const data = await getTimingsRequest({
      Race: race?.id || undefined,
      Statuses: selectedStatuses,
      SearchQuery: search || undefined,
    })
    setRows(data?.data || [])
    setLoading(false)
  }

  useEffect(() => { 
    load()
    // eslint-disable-next-line
  }, [race, selectedStatuses])

  const toggleStatus = (key) => {
    setSelectedStatuses(prev => prev.includes(key) ? prev.filter(s=>s!==key) : [...prev, key])
  }

  const act = async (type, id) => {
    setLoading(true)
    const map = { DQ: markDQRequest, Finished: markFinishedRequest, TBC: markTBCRequest, DNF: markDNFRequest, DNS: resetTimingRequest }
    await map[type]({ id })
    await load()
  }

  const openFinish = (row) => {
    setFinishRow(row)
    const start = row?.chip_start_time || ''
    const end = row?.chip_end_time || ''
    setChipStartTime(start)
    setChipEndTime(end)
    setHadTimes(Boolean(start && end))
    setFinishOpen(true)
  }

  const saveFinish = async () => {
    // If the record has no times, require both
    if (!hadTimes && (!chipStartTime || !chipEndTime)) return;
    setLoading(true)
    const payload = { id: finishRow.id }
    if (chipStartTime && chipEndTime) {
      payload.ChipStartTime = chipStartTime
      payload.ChipEndTime = chipEndTime
    }
    await markFinishedRequest(payload)
    setFinishOpen(false)
    await load()
  }

  return (
    <div className='page-content'>
      <Row>
        <Col xs={12}>
          <Card>
            <CardBody>
              <h4 className='mb-3'>Diagnostics</h4>
              <Row className='align-items-end'>
                <Col md={4}>
                  <Label>Race</Label>
                  <Input type='select' value={race?.id || ''} onChange={(e)=>{
                    const id = Number(e.target.value)
                    setRace(raceOptions.find(r=>r.id===id) || null)
                  }}>
                    <option value=''>All Timed Races</option>
                    {raceOptions.map(r=> <option key={r.id} value={r.id}>{r.name}</option>)}
                  </Input>
                </Col>
                <Col md={4}>
                  <Label>Statuses</Label>
                  <div>
                    {statuses.map(s=> (
                      <Badge key={s.key} color={selectedStatuses.includes(s.key) ? s.color : 'light'} onClick={()=>toggleStatus(s.key)} style={{cursor:'pointer'}} className='me-2'>{s.key}</Badge>
                    ))}
                  </div>
                </Col>
                <Col md={4}>
                  <Label>Search</Label>
                  <Input placeholder='Name or Bib' value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter') load() }} />
                </Col>
              </Row>
              <Row className='mt-3'>
                <Col xs={12} className='text-end'>
                  <Button color='primary' size='sm' onClick={load} disabled={loading}>Refresh</Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        {rows.map(r=> (
          <Col md={6} lg={4} key={r.id} className='mb-2'>
            <Card className='h-100'>
              <CardBody className='p-3'>
                <div className='d-flex justify-content-between'>
                  <div>
                    <div className='fw-bold'>{r.name}</div>
                    <small className='text-muted'>Bib {r.bib_number} {r.race?.name ? `• ${r.race.name}`: ''}</small>
                  </div>
                  <Badge color={statuses.find(s=>s.key===r.timing_status)?.color || 'secondary'}>{r.timing_status || '-'}</Badge>
                </div>
                <div className='mt-2'>
                  <div><small className='text-muted'>Chip: {r.chip_time || '-'}</small></div>
                  <div><small className='text-muted'>Start: {r.chip_start_time || '-'} • End: {r.chip_end_time || '-'}</small></div>
                  <div><small className='text-muted'>Overall: {r.overall_place || '-'} • Gender: {r.gender_place || '-'} • Cat: {r.age_category_place || '-'}</small></div>
                </div>
                <div className='d-flex gap-2 mt-3'>
                  <Button size='sm' color='danger' outline onClick={()=>act('DQ', r.id)} disabled={loading}>DQ</Button>
                  <Button size='sm' color='warning' outline onClick={()=>act('TBC', r.id)} disabled={loading}>TBC</Button>
                  <Button size='sm' color='secondary' outline onClick={()=>act('DNF', r.id)} disabled={loading}>DNF</Button>
                  <Button size='sm' color='success' onClick={()=>openFinish(r)} disabled={loading}>Finish</Button>
                  <Button size='sm' color='dark' outline onClick={()=>act('DNS', r.id)} disabled={loading}>Reset</Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal isOpen={finishOpen} toggle={()=>setFinishOpen(!finishOpen)} centered>
        <ModalHeader toggle={()=>setFinishOpen(!finishOpen)}>Mark as Finished</ModalHeader>
        <ModalBody>
          <p className='text-muted mb-3'>
            {hadTimes ? 'You can optionally adjust chip start/end times before finishing.' : 'This entry has no chip times. Please enter both start and end times.'}
          </p>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Chip Start Time</Label>
                <Input placeholder='HH:MM:SS.mmm' value={chipStartTime} onChange={(e)=>setChipStartTime(e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Chip End Time</Label>
                <Input placeholder='HH:MM:SS.mmm' value={chipEndTime} onChange={(e)=>setChipEndTime(e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={()=>setFinishOpen(false)} disabled={loading}>Cancel</Button>
          <Button color='success' onClick={saveFinish} disabled={loading || (!hadTimes && (!chipStartTime || !chipEndTime))}>
            {hadTimes ? 'Finish' : 'Finish with times'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
